import React, { useState } from 'react';
import { toast } from 'sonner';
import { Edit2, Save, X } from 'lucide-react';
import EmployeeLayout from '../../components/EmployeeLayout';
import { getEmployeeUser, setEmployeeUser } from '../../lib/employeeAuth';
import employeeApi from '../../lib/employeeApi';

export default function ProfilePage() {
  const employee = getEmployeeUser();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: employee?.personal?.full_name || '',
    mobile_number: employee?.personal?.mobile_number || '',
    personal_email: employee?.personal?.personal_email || '',
    aadhar_number: employee?.personal?.aadhar_number || '',
    aadhar_file_id: employee?.documents?.aadhar_file_id || '',
    pan_number: employee?.personal?.pan_number || '',
    pan_file_id: employee?.documents?.pan_file_id || '',
    bank_account_holder: employee?.bank_details?.account_holder_name || '',
    bank_ifsc: employee?.bank_details?.ifsc_code || '',
    bank_account_number: employee?.bank_details?.account_number || '',
    permanent_address: employee?.personal?.permanent_address || '',
    local_address: employee?.personal?.local_address || '',
    local_guardian_name: employee?.personal?.local_guardian_name || '',
    local_guardian_contact: employee?.personal?.local_guardian_contact || ''
  });

  const handleSave = async () => {
    try {
      const response = await employeeApi.put('/api/employee/profile/update', formData);
      setEmployeeUser(response.data);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  return (
    <EmployeeLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-charcoal-900">My Profile</h1>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 flex items-center gap-2">
              <Edit2 className="w-4 h-4" />Edit Profile
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => { setEditing(false); }} className="px-4 py-2 bg-charcoal-100 text-charcoal-700 rounded-xl hover:bg-charcoal-200 flex items-center gap-2">
                <X className="w-4 h-4" />Cancel
              </button>
              <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 flex items-center gap-2">
                <Save className="w-4 h-4" />Save Changes
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Info */}
          <div className="bg-white rounded-xl border border-charcoal-200 p-6">
            <h3 className="font-semibold text-charcoal-800 mb-4">Personal Information</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-charcoal-600">Full Name:</span> {editing ? <input type="text" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="font-medium ml-2">{employee?.personal?.full_name}</span>}</div>
              <div><span className="text-charcoal-600">Mobile:</span> {editing ? <input type="tel" value={formData.mobile_number} onChange={(e) => setFormData({...formData, mobile_number: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.personal?.mobile_number}</span>}</div>
              <div><span className="text-charcoal-600">Personal Email:</span> {editing ? <input type="email" value={formData.personal_email} onChange={(e) => setFormData({...formData, personal_email: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.personal?.personal_email}</span>}</div>
              <div><span className="text-charcoal-600">Work Email:</span> <span className="ml-2">{employee?.work_email}</span></div>
            </div>
          </div>

          {/* Employment */}
          <div className="bg-white rounded-xl border border-charcoal-200 p-6">
            <h3 className="font-semibold text-charcoal-800 mb-4">Employment</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-charcoal-600">Employee ID:</span> <span className="font-medium ml-2">{employee?.employment?.employee_id}</span></div>
              <div><span className="text-charcoal-600">Designation:</span> <span className="ml-2">{employee?.employment?.designation}</span></div>
              <div><span className="text-charcoal-600">Departments:</span> <span className="ml-2">{(employee?.employment?.departments || []).map((dept, i) => <span key={i} className="inline-block px-2 py-1 bg-pink-100 text-pink-700 rounded-xl text-xs mr-1">{dept}</span>)}</span></div>
              <div><span className="text-charcoal-600">CTC:</span> <span className="ml-2">₹{employee?.employment?.ctc?.toLocaleString('en-IN')}</span></div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-xl border border-charcoal-200 p-6">
            <h3 className="font-semibold text-charcoal-800 mb-4">Bank Details</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-charcoal-600">Account Holder:</span> {editing ? <input type="text" value={formData.bank_account_holder} onChange={(e) => setFormData({...formData, bank_account_holder: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.bank_details?.account_holder_name}</span>}</div>
              <div><span className="text-charcoal-600">IFSC:</span> {editing ? <input type="text" value={formData.bank_ifsc} onChange={(e) => setFormData({...formData, bank_ifsc: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.bank_details?.ifsc_code}</span>}</div>
              <div><span className="text-charcoal-600">Account Number:</span> {editing ? <input type="text" value={formData.bank_account_number} onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.bank_details?.account_number}</span>}</div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-charcoal-200 p-6">
            <h3 className="font-semibold text-charcoal-800 mb-4">Address</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-charcoal-600 block mb-1">Permanent:</span> {editing ? <textarea value={formData.permanent_address} onChange={(e) => setFormData({...formData, permanent_address: e.target.value})} rows={2} className="w-full px-2 py-1 border border-charcoal-200 rounded" /> : <span className="text-charcoal-700">{employee?.personal?.permanent_address}</span>}</div>
              <div><span className="text-charcoal-600 block mb-1">Local:</span> {editing ? <textarea value={formData.local_address} onChange={(e) => setFormData({...formData, local_address: e.target.value})} rows={2} className="w-full px-2 py-1 border border-charcoal-200 rounded" /> : <span className="text-charcoal-700">{employee?.personal?.local_address}</span>}</div>
              <div><span className="text-charcoal-600">Guardian Name:</span> {editing ? <input type="text" value={formData.local_guardian_name} onChange={(e) => setFormData({...formData, local_guardian_name: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.personal?.local_guardian_name}</span>}</div>
              <div><span className="text-charcoal-600">Guardian Contact:</span> {editing ? <input type="tel" value={formData.local_guardian_contact} onChange={(e) => setFormData({...formData, local_guardian_contact: e.target.value})} className="ml-2 px-2 py-1 border border-charcoal-200 rounded" /> : <span className="ml-2">{employee?.personal?.local_guardian_contact}</span>}</div>
            </div>
          </div>
        </div>
      </div>
    </EmployeeLayout>
  );
}